import React from 'react';
import './UITenancyOverwiev.scss';
import UIIconButton from '../UIIconButton/UIIconButton';
import UIUnit from '../UIUnit/UIUnit';

// ========== INTERFACES ==========

interface UITenancyOverviewProps {
  title?: string;
  viewAllText?: string;
  onViewAll?: () => void;
  properties: TenancyProperty[];
}

export type PropertyType = 'residential' | 'commercial' | 'mixed' | undefined | null;

export interface TenancyProperty {
  id: string;
  name: string;
  type: PropertyType;
  address: string;
  unit: string;
  latitude?: number | null;
  longitude?: number | null;
  payment: TenancyPaymentInfo;
  hasDebt?: boolean;
  onPropertyInfo?: (propertyId: string) => void;
  onContactProperty?: (propertyId: string) => void;
  onReportWarning?: (propertyId: string) => void;
  onMakePayment?: (propertyId: string) => void;
}

export interface TenancyPaymentInfo {
  status: 'PAID' | 'PENDING' | 'OVERDUE' | 'DEBT';
  rental: string;
  debt: string;
  nextPayment: string;
  leaseExpiration: string;
  paymentMethod: string;
  warnings: number;
  canPayOnline: boolean;
}

// ========== SVG ICONS ==========

const PaymentIcon: React.FC = () => (
  <svg
    width="15"
    height="24"
    viewBox="0 0 15 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.5 1V23M12.5 5H5C4.07174 5 3.1815 5.36875 2.52513 6.02513C1.86875 6.6815 1.5 7.57174 1.5 8.5C1.5 9.42826 1.86875 10.3185 2.52513 10.9749C3.1815 11.6313 4.07174 12 5 12H10C10.9283 12 11.8185 12.3688 12.4749 13.0251C13.1313 13.6815 13.5 14.5717 13.5 15.5C13.5 16.4283 13.1313 17.3185 12.4749 17.9749C11.8185 18.6313 10.9283 19 10 19H1.5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ========== COMPONENT ==========

const UITenancyOverview: React.FC<UITenancyOverviewProps> = ({
  title = 'Tenancy overview',
  viewAllText = 'View all',
  onViewAll,
  properties,
}) => {
  // ========== RENDER HELPERS ==========

  const renderStatusPill = (status: string, isWarning?: boolean) => {
    const pillClass = isWarning
      ? 'UITenancyOverview__pill UITenancyOverview__pill--danger'
      : status === 'DEBT'
        ? 'UITenancyOverview__pill UITenancyOverview__pill--danger'
        : 'UITenancyOverview__pill UITenancyOverview__pill--success';

    return <div className={pillClass}>{status}</div>;
  };

  const renderPaymentButton = (property: TenancyProperty) => {
    return (
      <UIIconButton
        className="UITenancyOverview__payment-button"
        variant="success"
        icon={<PaymentIcon />}
        text="Online payment"
        onClick={() => property.onMakePayment?.(property.id)}
        disabled={!property.payment.canPayOnline}
      />
    );
  };

  // ========== COMPONENT MARKUP ==========

  return (
    <div className="UITenancyOverview">
      <div className="UITenancyOverview__title">
        <div className="UITenancyOverview__content">
          <h4 className="UITenancyOverview__heading">{title}</h4>
          {onViewAll && (
            <button
              className="UITenancyOverview__view-all-button"
              onClick={onViewAll}
            >
              {viewAllText}
            </button>
          )}
        </div>
        <div className="UITenancyOverview__line"></div>
      </div>

      <div className="UITenancyOverview__properties">
        {properties.map((property) => {
          return (
            <div key={property.id} className="UITenancyOverview__property">
              <UIUnit property={property} />

              <div className="UITenancyOverview__payment-info">
                <div className="UITenancyOverview__payment-content">
                  <div className="UITenancyOverview__payment-table">
                    <div className="UITenancyOverview__field-names">
                      <div className="UITenancyOverview__field-name">
                        Payment:
                      </div>
                      <div className="UITenancyOverview__field-name">Rental:</div>
                      <div className="UITenancyOverview__field-name">Debt:</div>
                      <div className="UITenancyOverview__field-name">
                        Next payment:
                      </div>
                      <div className="UITenancyOverview__field-name">
                        Lease expiration:
                      </div>
                      <div className="UITenancyOverview__field-name">
                        Payment method:
                      </div>
                      <div className="UITenancyOverview__field-name">
                        Warnings:
                      </div>
                    </div>
                    <div className="UITenancyOverview__field-infos">
                      <div className="UITenancyOverview__field-info">
                        {renderStatusPill(property.payment.status)}
                      </div>
                      <div className="UITenancyOverview__field-info">
                        {property.payment.rental}
                      </div>
                      <div
                        className={`UITenancyOverview__field-info ${property.payment.debt !== '$ 0.00' ? 'UITenancyOverview__field-info--danger' : 'UITenancyOverview__field-info--success'}`}
                      >
                        {property.payment.debt}
                      </div>
                      <div className="UITenancyOverview__field-info">
                        {property.payment.nextPayment}
                      </div>
                      <div className="UITenancyOverview__field-info">
                        {property.payment.leaseExpiration}
                      </div>
                      <div className="UITenancyOverview__field-info">
                        {property.payment.paymentMethod}
                      </div>
                      <div className="UITenancyOverview__field-info">
                        {renderStatusPill(
                          property.payment.warnings.toString(),
                          property.payment.warnings > 0,
                        )}
                      </div>
                    </div>
                  </div>

                  {renderPaymentButton(property)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UITenancyOverview;
